import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { SEOMeta } from '@/components/SEOMeta';
import { PageBreadcrumb } from '@/components/PageBreadcrumb';

const BREADCRUMBS = [{ label: 'Контакти', href: '/contact' }];

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Съобщението е изпратено. Ще се свържем с вас скоро!');
    setForm({ name: '', email: '', message: '' });
  };
  return (
    <>
      <SEOMeta
        title="Контакти"
        description="Свържи се с екипа на portion.bg за въпроси, предложения или техническа поддръжка. Имейл: info@portion.bg"
        keywords="portion.bg контакти, поддръжка хранителен план, контактна форма portion"
        canonical="/contact"
        breadcrumbs={BREADCRUMBS}
        schemas={[
          {
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            '@id': 'https://portion.bg/contact#webpage',
            url: 'https://portion.bg/contact',
            name: 'Контакти — portion.bg',
            description: 'Свържи се с екипа на portion.bg за въпроси, предложения или техническа поддръжка.',
            inLanguage: 'bg-BG',
            isPartOf: { '@id': 'https://portion.bg/#website' },
          },
        ]}
      />
      <PageBreadcrumb items={BREADCRUMBS} />
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <div className="text-center mb-10">
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border mb-4">Контакти</Badge>
          <h1 className="text-3xl font-semibold text-gray-900 dark:text-gray-100 mb-3">Свържи се с нас</h1>
          <p className="text-slate-500 dark:text-slate-400">Имаш въпрос или проблем? Ще ти отговорим.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
          {[
            { icon: Mail, label: 'Имейл', val: 'info@portion.bg' },
            { icon: Phone, label: 'Телефон', val: '+359 88 000 0000' },
            { icon: MapPin, label: 'Офис', val: 'София, България' },
          ].map(c => (
            <Card key={c.label} className="card-shadow rounded-xl">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <c.icon className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-400">{c.label}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{c.val}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <Card className="card-shadow rounded-xl">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-900 dark:text-gray-100 text-sm font-medium">Име</Label>
                  <Input placeholder="Име Тегло" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} className="mt-1.5 h-11" required />
                </div>
                <div>
                  <Label className="text-gray-900 dark:text-gray-100 text-sm font-medium">Имейл</Label>
                  <Input type="email" placeholder="you@example.com" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} className="mt-1.5 h-11" required />
                </div>
              </div>
              <div>
                <Label className="text-gray-900 dark:text-gray-100 text-sm font-medium">Съобщение</Label>
                <textarea value={form.message} onChange={e=>setForm(f=>({...f,message:e.target.value}))} placeholder="Напишете съобщението си..." rows={4} required
                  className="mt-1.5 w-full rounded-lg border-2 border-[#E6EDF5] bg-white dark:bg-slate-800 p-3 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:border-emerald-500 transition-colors resize-none" />
              </div>
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white min-h-11">Изпрати</Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
    </>
  );
}
